import { portalAPI } from './portalAPI';

import type { MfaStatus, UpdateUserPreferences, UserData } from 'types/user';
import { broadcastImpersonationChange } from 'utils/impersonationBroadcast';

export const APIUserSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: ['CurrentUser', 'MfaStatus'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            // Get the current user data by JWT
            getUserData: builder.query<UserData, void>({
                query: () => `/v1/users/current`,
                keepUnusedDataFor: 10 * 60,
                providesTags: ['CurrentUser'],
            }),
            impersonateUser: builder.mutation<UserData, number>({
                query: (id) => ({
                    url: `/v1/users/${id}`,
                    method: 'PUT',
                    body: {},
                }),
                // No invalidatesTags: AWSCognitoContext.impersonateUser dispatches
                // portalAPI.util.resetApiState() once the callback fires, which drops
                // every cached query including all the per-user ones.
                async onQueryStarted(_id, api) {
                    try {
                        await api.queryFulfilled;
                    } catch {
                        // Failures surface at call sites via unwrap(); just skip the broadcast
                        return;
                    }
                    broadcastImpersonationChange();
                },
            }),
            stopImpersonation: builder.mutation<UserData, void>({
                query: () => ({
                    url: `/v1/users/stop-impersonation`,
                    method: 'PUT',
                    body: {},
                }),
                async onQueryStarted(_arg, api) {
                    try {
                        await api.queryFulfilled;
                    } catch {
                        return;
                    }
                    broadcastImpersonationChange();
                },
            }),
            getMfaStatus: builder.query<MfaStatus, void>({
                query: () => `/v1/users/current/mfa`,
                providesTags: ['MfaStatus'],
            }),
            recordMfaEnrolment: builder.mutation<MfaStatus, void>({
                query: () => ({
                    url: `/v1/users/current/mfa/enrolment`,
                    method: 'POST',
                }),
                invalidatesTags: ['MfaStatus'],
            }),
            updateCurrentUserPreferences: builder.mutation<UserData, UpdateUserPreferences>({
                query: (preferences) => ({
                    url: `/v1/users/current/preferences`,
                    method: 'PUT',
                    body: preferences,
                }),
                invalidatesTags: ['CurrentUser'],
            }),
            // The same write for the design switches and the welcome popover, which confirm nothing on success. Their
            // caller patches the cached user first, so nothing is read back: a read answering after a newer patch
            // would undo it.
            saveCurrentUserPreferencesQuietly: builder.mutation<UserData, UpdateUserPreferences>({
                query: (preferences) => ({
                    url: `/v1/users/current/preferences`,
                    method: 'PUT',
                    body: preferences,
                }),
            }),
        }),
        overrideExisting: false,
    });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetUserDataQuery,
    useGetMfaStatusQuery,
    useRecordMfaEnrolmentMutation,
    useImpersonateUserMutation,
    useStopImpersonationMutation,
    useUpdateCurrentUserPreferencesMutation,
    useSaveCurrentUserPreferencesQuietlyMutation,
} = APIUserSlice;
